import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050A15] px-6 py-16 text-white">
      <div className="mx-auto max-w-md">
        <Logo />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="mt-16">
          {done ? (
            <div className="rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-8 text-center" data-testid="forgot-done">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37]/20">
                <Check className="h-6 w-6 text-[#D4AF37]" />
              </span>
              <h1 className="mt-6 font-serif text-3xl font-light">Controlla la tua email</h1>
              <p className="mt-3 text-white/60">
                Se l'indirizzo <strong className="text-white">{email}</strong> è registrato, ti abbiamo inviato un link per
                reimpostare la password. Il link è valido per 1 ora.
              </p>
              <p className="mt-4 text-sm text-white/40">
                Non trovi l'email? Controlla la cartella spam o riprova tra qualche minuto.
              </p>
              <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:underline">
                Torna al login <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <>
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Password dimenticata</p>
              <h1 className="mt-4 font-serif text-4xl font-light">Reimposta la tua password</h1>
              <p className="mt-3 text-white/60">
                Inserisci l'email del tuo account SelfBook Italia e ti mandiamo un link per scegliere una nuova password.
              </p>
              <form onSubmit={submit} className="mt-10 space-y-6">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/40">Email</Label>
                  <div className="relative mt-2">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="nome@email.it"
                      data-testid="forgot-email-input"
                      className="border-white/10 bg-white/5 pl-10 text-white placeholder:text-white/25"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={busy} data-testid="forgot-submit"
                  className="w-full gap-2 rounded-full bg-[#D4AF37] text-[#050A15] hover:bg-[#E5C158]">
                  {busy ? "Invio in corso..." : "Invia link di reimpostazione"} <ArrowRight className="h-4 w-4" />
                </Button>
              </form>
              <p className="mt-8 text-sm text-white/50">
                Ti sei ricordato la password? <Link to="/login" className="text-[#D4AF37] hover:underline">Torna al login</Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
